import * as signalR from "@microsoft/signalr";

export let connection: signalR.HubConnection;
let startPromise: Promise<void> | null = null;
let activeCaseIdGroup: string | null = null;
let messageHandler: ((message: any) => void) | null = null;

let presenceHandler: ((data: any) => void) | null = null;
let typingHandler: ((data: any) => void) | null = null;

export type ChatConnectionState = "connected" | "reconnecting" | "disconnected";
let connectionState: ChatConnectionState = "disconnected";

const setConnectionState = (state: ChatConnectionState) => {
  connectionState = state;
  window.dispatchEvent(new CustomEvent<ChatConnectionState>("ChatConnectionStateChanged", { detail: state }));
};

export const getConnectionState = (): ChatConnectionState => connectionState;

// Reconnect handler attached once to the HubConnection
const handleReconnected = async () => {
  setConnectionState("connected");
  if (messageHandler && connection) {
    connection.off("ReceiveMessage");
    connection.on("ReceiveMessage", messageHandler);
  }
  if (presenceHandler && connection) {
    connection.off("UserPresenceChanged");
    connection.on("UserPresenceChanged", (data: any) => {
      window.dispatchEvent(new CustomEvent("UserPresenceChanged", { detail: data }));
      if (presenceHandler) presenceHandler(data);
    });
  }
  if (typingHandler && connection) {
    connection.off("UserTyping");
    connection.on("UserTyping", (data: any) => {
      window.dispatchEvent(new CustomEvent("UserTyping", { detail: data }));
      if (typingHandler) typingHandler(data);
    });
  }
  if (activeCaseIdGroup && connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("JoinCaseChat", activeCaseIdGroup);
    } catch (err) {
      console.warn("Error re-joining chat group after reconnect:", err);
    }
  }
};

export const initializeChatHub = (token: string) => {
  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) {
      // Connection was already open (e.g. re-entering a chat screen) — these branches used to
      // return without ever updating connectionState, which could leave the "Disconnected" banner
      // stuck forever even though the connection was actually fine.
      setConnectionState("connected");
      return Promise.resolve();
    }
    if (connection.state === signalR.HubConnectionState.Connecting || connection.state === signalR.HubConnectionState.Reconnecting) {
      return startPromise || Promise.resolve();
    }
    if (connection.state === signalR.HubConnectionState.Disconnected) {
      startPromise = connection.start().then(() => {
        startPromise = null;
        setConnectionState("connected");
      }).catch(err => {
        startPromise = null;
        setConnectionState("disconnected");
        throw err;
      });
      return startPromise;
    }
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${process.env.REACT_APP_BASE_URL}/chatHub`, {
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      accessTokenFactory: () => (localStorage.getItem("token") || "").replace(/(^"|"$)/g, ''),
    })
    // Default withAutomaticReconnect() only retries at [0, 2s, 10s, 30s] and then gives up for good,
    // permanently disconnecting the tab until the user manually refreshes. Retry indefinitely instead,
    // matching the mobile client's backoff, so a longer server blip/deploy self-heals.
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) =>
        Math.min(2000 * Math.pow(1.5, retryContext.previousRetryCount), 30000),
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();

  connection.serverTimeoutInMilliseconds = 60000;
  connection.keepAliveIntervalInMilliseconds = 30000;

  connection.off("UserPresenceChanged");
  connection.on("UserPresenceChanged", (data: any) => {
    window.dispatchEvent(new CustomEvent("UserPresenceChanged", { detail: data }));
    if (presenceHandler) presenceHandler(data);
  });

  connection.off("UserTyping");
  connection.on("UserTyping", (data: any) => {
    window.dispatchEvent(new CustomEvent("UserTyping", { detail: data }));
    if (typingHandler) typingHandler(data);
  });

  connection.onreconnecting(() => setConnectionState("reconnecting"));
  connection.onreconnected(handleReconnected);
  connection.onclose(() => setConnectionState("disconnected"));

  startPromise = connection.start().then(() => {
    startPromise = null;
    setConnectionState("connected");
  }).catch(err => {
    startPromise = null;
    setConnectionState("disconnected");
    throw err;
  });

  return startPromise;
};

// Helper to ensure connection is ready before any invoke
export const ensureConnected = async () => {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    const token = localStorage.getItem("token") || "";
    await initializeChatHub(token);
    return;
  }

  if (connection.state === signalR.HubConnectionState.Connected) {
    return;
  }

  if (startPromise) {
    await startPromise;
  }
};

// Use this to ensure connection is active without re-initializing if unnecessary
export const ensureGlobalConnection = async (token: string) => {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    return await initializeChatHub(token);
  }
};

export const joinChatRoom = async (caseId: string) => {
  try {
    await ensureConnected();
    activeCaseIdGroup = caseId;
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke("JoinCaseChat", caseId);
    }
  } catch (err) {
    console.warn("SignalR: Failed to join case chat room:", err);
  }
};

export const leaveChatRoom = async (caseId: string) => {
  if (activeCaseIdGroup === caseId) {
    activeCaseIdGroup = null;
  }
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("LeaveCaseChat", caseId);
    } catch (err) {
      console.warn("Failed to leave chat room:", err);
    }
  }
};

export const sendMessage = async (
  caseId: number | null,
  senderUserId: number | null,
  content: string,
  type: string = "text",
  attachmentUrl: string | null = "",
  fileName: string | null = ""
) => {
  await ensureConnected();
  await connection.invoke(
    "SendMessage",
    caseId,
    senderUserId,
    content,
    type,
    attachmentUrl,
    fileName
  );
};

export const onReceiveMessage = (handler: (message: any) => void) => {
  messageHandler = handler;
  if (connection) {
    connection.off("ReceiveMessage");
    connection.on("ReceiveMessage", handler);
  }
};

export const deleteMessage = async (messageId: number, caseId: number) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    await connection.invoke("DeleteMessage", Number(messageId), Number(caseId));
  }
};

export const onMessageDeleted = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("MessageDeleted");
    connection.on("MessageDeleted", (msgId: any, cId: any) => {
      handler({ messageId: Number(msgId), caseId: Number(cId) });
    });
  }
};

export const offMessageDeleted = () => {
  if (connection) {
    connection.off("MessageDeleted");
  }
};

export const disconnectChatHub = async () => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.stop();
    } catch (err) {
      console.warn("Error while disconnecting SignalR:", err);
    }
  }
};

export const sendSOSAlert = async (caseId: any, senderId: any) => {
  const nCaseId = Number(caseId);
  const nUserId = Number(senderId);

  if (isNaN(nCaseId) || isNaN(nUserId)) {
    console.error("SOS Failed: Invalid IDs", { caseId, senderId });
    return;
  }

  try {
    await ensureConnected();
    if (connection && connection.state === signalR.HubConnectionState.Connected) {
      await connection.invoke("TriggerSOS", nCaseId, nUserId);
    }
  } catch (err) {
    console.error("SignalR TriggerSOS Error:", err);
    throw err;
  }
};

export const onReceiveSOS = (callback: (data: any) => void) => {
  if (connection) {
    connection.off("AdminNotified");
    connection.on("AdminNotified", callback);
  }
};

// Global listener for new messages (for notifications)
export const onReceiveGlobalMessage = (handler: (message: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveMessageGlobal");
  connection.on("ReceiveMessageGlobal", handler);
};

export const onReceiveGlobalSOS = (handler: (data: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveGlobalAdminAlert");
  connection.on("ReceiveGlobalAdminAlert", handler);
};

export const offReceiveGlobalSOS = () => {
  if (connection) {
    connection.off("ReceiveGlobalAdminAlert");
  }
};

export const onReceiveNotification = (handler: (data: any) => void) => {
  if (!connection) return;
  connection.off("ReceiveNotification");
  connection.on("ReceiveNotification", handler);
};

export const offReceiveNotification = () => {
  if (connection) {
    connection.off("ReceiveNotification");
  }
};

// --- Read Receipts ---
export const markAsRead = async (caseId: number, userId: number) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("MarkAsRead", caseId, userId);
    } catch (err) {
      console.error("Failed to invoke MarkAsRead", err);
    }
  }
};

export const onReceiveReadReceipt = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("MessagesRead");
    connection.on("MessagesRead", handler);
  }
};

export const offReceiveReadReceipt = () => {
  if (connection) {
    connection.off("MessagesRead");
  }
};

// --- Chat List Global Listeners (for unread badge counting outside the active chat room) ---

/**
 * Register a listener to receive ALL incoming messages (across all joined rooms).
 * Used by the chat list to increment unread counts for cases not currently open.
 * Pass `null` to unregister.
 */
export const onGlobalReceiveMessage = (handler: ((message: any) => void) | null) => {
  if (!connection) return;
  connection.off("ReceiveMessage_global_list");
  if (handler) {
    // We piggyback on the existing "ReceiveMessage" event; we register a separate named copy
    // using a closure so the SingleChat handler is not displaced
    connection.off("ReceiveMessage_global_list");
    // Use the raw connection to add a second listener for the same event
    (connection as any)._handlers = (connection as any)._handlers || {};
    // Register via on() with a unique tracking key via off+on pattern
    // Since SignalR allows multiple handlers, we just call on() again
    connection.on("ReceiveMessage", handler);
  }
};

export const offGlobalReceiveMessage = (handler: ((message: any) => void)) => {
  if (connection && handler) {
    connection.off("ReceiveMessage", handler);
  }
};

/**
 * Register a listener for MessagesRead events from the server.
 * Used by the chat list to clear unread counts when another user (or same user in another tab) opens a chat.
 * Pass `null` to unregister.
 */
export const onGlobalMessagesRead = (handler: ((data: any) => void) | null) => {
  if (!connection) return;
  if (handler) {
    connection.on("MessagesRead", handler);
  }
};

export const offGlobalMessagesRead = (handler: ((data: any) => void)) => {
  if (connection && handler) {
    connection.off("MessagesRead", handler);
  }
};

export const onReceiveCallInvite = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("IncomingCall");
    connection.on("IncomingCall", handler);
  }
};

export const offReceiveCallInvite = () => {
  if (connection) {
    connection.off("IncomingCall");
  }
};

export const onReceiveCallEnded = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("CallEnded");
    connection.on("CallEnded", handler);
  }
};

export const offReceiveCallEnded = () => {
  if (connection) {
    connection.off("CallEnded");
  }
};

// Backend broadcasts this to Clients.User(id) — every one of THIS user's own live
// connections/tabs/devices — the moment one of them successfully joins the call. Used to stop
// a still-ringing tab/device on this same account (e.g. this web tab was ringing while the
// user answered on mobile) instead of it ringing forever with no signal that the call was
// already picked up elsewhere.
export const onReceiveCallAnswered = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("CallAnswered");
    connection.on("CallAnswered", handler);
  }
};

export const offReceiveCallAnswered = () => {
  if (connection) {
    connection.off("CallAnswered");
  }
};

export const onReceiveCallRejected = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("CallRejected");
    connection.on("CallRejected", handler);
  }
};

export const offReceiveCallRejected = () => {
  if (connection) {
    connection.off("CallRejected");
  }
};

export const rejectCall = async (caseId: number | string, userId: number | string) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("RejectCall", Number(caseId), Number(userId));
    } catch (err) {
      console.warn("SignalR RejectCall error:", err);
    }
  }
};

export const onCallParticipantsUpdated = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("CallParticipantsUpdated");
    connection.on("CallParticipantsUpdated", handler);
  }
};

export const offCallParticipantsUpdated = () => {
  if (connection) {
    connection.off("CallParticipantsUpdated");
  }
};

export const onUserPresenceChanged = (handler: (data: any) => void) => {
  presenceHandler = handler;
  if (connection) {
    connection.off("UserPresenceChanged");
    connection.on("UserPresenceChanged", handler);
  }
};

export const offUserPresenceChanged = () => {
  presenceHandler = null;
  if (connection) {
    connection.off("UserPresenceChanged");
  }
};

export const onUserTyping = (handler: (data: any) => void) => {
  typingHandler = handler;
  if (connection) {
    connection.off("UserTyping");
    connection.on("UserTyping", handler);
  }
};

export const offUserTyping = () => {
  typingHandler = null;
  if (connection) {
    connection.off("UserTyping");
  }
};

export const sendTypingStatus = async (caseId: number | string, isTyping: boolean) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("SendTypingStatus", Number(caseId), isTyping);
    } catch (err) {
      console.warn("SignalR SendTypingStatus error:", err);
    }
  }
};

